import { HttpClient } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { catchError, debounceTime, delay, filter, map, retry, switchMap, take } from 'rxjs/operators';
import { defer, fromEvent, merge, Observable, of, timer } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { awsConfig } from '../../../../../../../libs/includes/aws-export';


export interface DeviceCheckInput {
  lastActive: number;
  lastPageViewed: string;
  metadata: {
    platform: string;
    manufacturer?: string;
    model?: string;
    deviceName?: string;
    osVersion?: string;
    webViewVersion?: string;
    serialNumber: string;
    macAddress: string;
    memUsed: number;
  }
}

@Injectable({ providedIn: 'root' })
export class PlatformService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private versionUrl = `${window.location.origin}/version.json`;
  private localKey = 'version_timestamp';


  constructor() {
    if (!isDevMode()) {
      this.checkForUpdate().subscribe(res => {
        if (res.updateAvailable) {
          console.log('[PlatformService] Version changed, reloading...');
          window.location.reload();
        }
      })

      merge(
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd)
        ),
        fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState === 'visible')
        )
      ).pipe(
        debounceTime(1000), // optional, to avoid duplicate checks
        takeUntilDestroyed()
      ).subscribe(() => {
        this.checkForUpdate().subscribe(res => {
          if (res.updateAvailable) {
            console.log('[PlatformService] Version changed, reloading...');
            window.location.reload();
          }
        })
      });
    }
  }

  checkForUpdate(): Observable<{ updateAvailable: boolean }> {
    const makeUrl = () => `${this.versionUrl}?v=${Date.now() % 1_000_000}`;
    const whenOnline$ = fromEvent(window, 'online').pipe(take(1));

    const fetch$ = defer(() => this.http.get<{ timestamp: number }>(makeUrl())).pipe(
      map(remote => {
        const remoteTs = remote.timestamp;
        const storedTs = Number(localStorage.getItem(this.localKey));
        const updateAvailable = !!(storedTs && remoteTs !== storedTs);
        localStorage.setItem(this.localKey, String(remoteTs));
        return { updateAvailable };
      }),
      retry({
        count: 2,
        delay: (err) => {
          const status = err?.status;
          // Retry network errors (status 0) and server errors (5xx)
          if (status === 0 || (status >= 500 && status < 600)) {
            return timer(500);
          }
          throw err;
        }
      })
    );

    return (navigator.onLine ? fetch$ : whenOnline$.pipe(switchMap(() => fetch$))).pipe(
      delay(300),
      catchError(err => {
        const isNetworkError = err?.status === 0;

        const logFn = isNetworkError ? console.warn : console.error;
        logFn('Failed to fetch version.json', {
          url: makeUrl(),
          status: err?.status,
          statusText: err?.statusText,
          online: navigator.onLine
        });

        // For status 0, treat as transient: don't trigger Slack; no update available.
        return of({ updateAvailable: false });
      })
    );
  }

}